import { format, subDays } from 'date-fns'
import { generateTrackingID } from 'helpers'
import config from 'const/config'
import {
  AddBuyerRequestParams,
  AuthParams,
  BuyerParams,
  BuyerRequestBodyParams,
  DeactiveOnlineUserRequestParams,
  EbillEnrollmentParams,
  EbillProfileParams,
  GetCliAutoEligibilityParams,
  HeaderParams,
  InvoiceDownloadRequestParams,
  PartialRegistrationRequestParams,
  PrintInvoiceRequestParams,
  RegistrationAuthParams,
  RegistrationEligibilityCheckRequestParams,
  RemoveBuyerRequestParams,
  SaveRegistrationRequestParams,
  SchedulePaymentRequestParams,
  SendNewMessageParams,
  ShoppingCartParams,
  ShoppingCartRequestDataParams,
  SwitchAccountInfoParams,
  SwitchLinkedAccountsParams,
  TransactionParams,
  TransactionRequestBodyParams,
  UpdatePasswordParams,
  UpdatePrivilegeRequestParams
} from 'typings/apiRequest'
import { InvoiceDownloadRequest } from 'typings/invoices'
import {
  EditPaymentRequestParams,
  PaymentInfo,
  SchedulePaymentRequest
} from 'typings/payment'
import { AddBuyerRequest, DeleteBuyerRequest } from 'typings/buyer'
import { SendNewMessage } from 'typings/message'
import { EbillInfo } from 'typings/ebill'
import { AnyObject } from 'typings/custom'
import { getCardType, userAccountInfo } from 'helpers/user'
import { OnlineUserListRequest } from 'typings/onlineUser'
import { PrintInvoiceRequest } from 'typings/invoiceSummary'
import { SwitchAccountRequest } from 'typings/linkedAccount'
import {
  PostLoginUpdatePasswordParams,
  PostLoginUpdatePasswordRequest,
  UpdatePasswordRequest
} from 'typings/resetPassword'
import {
  PartialSaveUserRegistrationRequest,
  RegistrationRequest,
  SaveUserRegistrationRequest,
  UpdatePrivilegeRequest
} from 'typings/registration'
import { SwitchAccountRequestBody } from 'services/switchAccount/typings'
import {
  CSRAccountParams,
  CSRAccountRequestBodyParams
} from 'typings/csrNotification'
import {
  CliAutoEligibilityRequest,
  CliRequestBodyParams,
  CliRequestPayload
} from 'typings/cliRequest'
import { GetAuthTokenParams, GetAuthTokenRequest } from 'services/authToken'
import { getCodeChallenge, getCodeVerifier } from 'services/codeVerifier'

const { APIGEE_CLIENT_ID } = config

export const redirectSynchronyCredit = 'https://redirect.synchronycredit.com'
export const authState = 'V1o3Wnk1ZEN5TkR6cWZ5NG8xWmZEanFhUm1vQW9XZG'
export const contentTypeJson = 'application/json'

export const codeChallenge = getCodeChallenge()
export const codeChallengeMethod = 'S256'

// This auth Info is used only for the switch account flow
const switchAccountAuthInfo: AuthParams = {
  clientId: APIGEE_CLIENT_ID,
  redirectUri: redirectSynchronyCredit,
  scope: 'login',
  state: authState,
  codeChallenge,
  codeChallengeMethod,
  responseType: 'code'
}

// This auth Info is used only for the registration flow
const authInformation: RegistrationAuthParams = {
  appId: APIGEE_CLIENT_ID,
  redirectUri: redirectSynchronyCredit,
  scope: 'registration',
  state: authState,
  codeChallenge,
  codeChallengeMethod
}

/**
 * Helper for sharing auth info in multiple api request bodies.
 */
export const getAuthInfo = () => authInformation

export const getCorrelationID = (): string =>
  userAccountInfo.trackingID || generateTrackingID()

export const getHeaderParams = (
  accessToken?: string,
  method = '',
  hasAccountType = false
): HeaderParams => {
  const { accountType } = userAccountInfo
  const headerParams: HeaderParams = {
    'X-SYF-Request-TrackingId': generateTrackingID(),
    'X-SYF-Request-CorrelationId': getCorrelationID()
  }

  if (accessToken) {
    headerParams.Authorization = `Bearer ${accessToken}`
  }
  if (!method) {
    headerParams['Content-Type'] = contentTypeJson
  }
  if (hasAccountType) {
    headerParams.accountType = accountType
  }
  return headerParams
}

export const getRegistrationHeaderParams = (
  accessToken: string,
  accountType: string
): HeaderParams => {
  const headerParams = getHeaderParams(accessToken)
  headerParams.accountType = accountType
  return headerParams
}

export const getBuyerParams = (params: BuyerParams): BuyerRequestBodyParams => {
  const { accountNumber, type = 'AUTH_BUYER', status = 'A' } = params
  return {
    'cipher.accountId': accountNumber,
    type,
    status,
    requestFields: {
      lookupField: []
    }
  }
}

export const shoppingCartDataParams: ShoppingCartRequestDataParams = {
  requestFields: {
    lookupField: ['accountLookup']
  },
  loginUser: 'ESERVICE',
  phoneNumber: '',
  transactionNumber: '',
  startDate: '',
  endDate: '',
  transactionStatus: '',
  transactionType: '',
  purchaseOrderNumber: '',
  authorisedBuyerName: '',
  storeNumber: '',
  lowestAmount: '',
  highestAmount: '',
  page: '1',
  sortType: '',
  sortBy: '',
  recordCount: '',
  'cipher.accountId': '',
  accountIdType: 'guid'
}

export const getShoppingCartParamsforTransaction = (
  dataParams: TransactionParams
): TransactionRequestBodyParams => {
  const { accountNumber, transactionNumber, transactionStatus } = dataParams
  return {
    'cipher.accountId': accountNumber,
    transactionNumber: transactionNumber ? `${transactionNumber}%` : null,
    transactionStatus,
    requestFields: {
      lookupField: ['includeTransactionDetails']
    },
    recordCount: '',
    sortType: '',
    sortBy: ''
  }
}

// eslint-disable-next-line max-lines-per-function
export const getShoppingCartParamsforInvoice = (
  dataParams: ShoppingCartParams,
  transactionType: string
): ShoppingCartRequestDataParams => {
  const {
    currentPage,
    sortColumn,
    sortOrder,
    filterValue,
    filterColumn,
    limit,
    type,
    accountNumber
  } = dataParams

  const searchValue = filterValue ? `${filterValue}%` : ''

  const updatedParams = {
    'cipher.accountId': accountNumber,
    accountIdType: 'guid',
    phoneNumber: '',
    loginUser: 'ESERVICE',
    transactionNumber: filterColumn === 'transactionId' ? searchValue : '',
    transactionStatus: transactionType,
    transactionType: '',
    purchaseOrderNumber: filterColumn === 'purchaseOrder' ? searchValue : '',
    authorisedBuyerName: '',
    storeNumber: '',
    lowestAmount: '',
    highestAmount: '',
    recordCount: (limit || '').toString(),
    page: (currentPage || '').toString(),
    sortBy: (sortColumn || '').toString(),
    sortType: (sortOrder || '').toString(),
    requestFields: {
      lookupField: ['invoiceLookup']
    },
    startDate:
      transactionType === 'CL'
        ? format(subDays(new Date(), 90), 'MMddyyyy')
        : '',
    endDate: transactionType === 'CL' ? format(new Date(), 'MMddyyyy') : ''
  }

  // Support returning specific types of transactions
  if (type === 'invoice') {
    updatedParams.transactionType = 'INV' // Invoice
  } else if (type === 'credit') {
    updatedParams.transactionType = 'CM' // Credit Memo
  }

  return updatedParams
}

export const getShoppingCartParams = (
  dataParams: Partial<ShoppingCartParams>,
  lookupFields: Array<string>
): ShoppingCartRequestDataParams => {
  const { accountNumber } = dataParams
  return {
    ...shoppingCartDataParams,
    'cipher.accountId': accountNumber,
    requestFields: {
      lookupField: lookupFields
    }
  }
}

export const csrAccountUpdateParams = {
  accountType: 'PROX',
  customerName: '',
  phoneNumber: '',
  emailAddress: '',
  companyName: ''
}

export const getCSRAccountUpdateParams = (
  dataParams: CSRAccountParams
): CSRAccountRequestBodyParams => {
  const {
    accountNumber,
    phoneNumber,
    emailAddress,
    creditLine,
    statement,
    type,
    customerName,
    companyName
  } = dataParams

  const { accountType, isProx, accountID } = userAccountInfo

  const updatedParams: CSRAccountRequestBodyParams = {
    ...csrAccountUpdateParams,
    'cipher.accountId': isProx ? accountNumber : accountID,
    phoneNumber,
    emailAddress,
    customerName,
    companyName,
    accountType,
    cardType: getCardType()
  }
  switch (type) {
    case 'CREDIT_LINE':
      updatedParams.creditLine = {
        ...creditLine
      }
      break
    case 'STATEMENT':
      updatedParams.statement = {
        ...statement
      }
      break
    default:
      break
  }
  return updatedParams
}

export const getLoginHeaderParams = (): HeaderParams => ({
  'Content-Type': contentTypeJson,
  'X-SYF-Request-TrackingId': generateTrackingID(),
  'X-SYF-Request-CorrelationId': getCorrelationID()
})

export const getUpdatePasswordParams = ({
  newPassword,
  sessionId
}: UpdatePasswordRequest): UpdatePasswordParams => ({
  'cipher.newPassword': newPassword,
  sessionId
})

export const getPostLoginUpdatePasswordParams = ({
  newPassword,
  currentPassword,
  userId
}: PostLoginUpdatePasswordRequest): PostLoginUpdatePasswordParams => ({
  newPassword,
  password: currentPassword,
  userId
})

type AuthBuyerParams = {
  authBuyer: Array<AddBuyerRequestParams>
  'cipher.accountId'?: string
}

export const getAuthBuyerList = (
  payload: AddBuyerRequest,
  accountList: Array<AnyObject>,
  cardStatus = 'N'
): AddBuyerRequestParams => {
  const {
    newBuyer: { firstName, lastName, phoneNumber, phoneConsent, emailAddress }
  } = payload
  return {
    accounts: [
      ...accountList.map(
        ({ cipherAccountNo, companyName, accountUniqueIdentifier }) => {
          return {
            'cipher.accountNumber': cipherAccountNo,
            accountUniqueIdentifier,
            name: companyName,
            phoneNumber
          }
        }
      )
    ],
    buyers: [
      {
        firstName,
        lastName,
        phoneNumber,
        phoneConsent,
        emailAddress,
        card: cardStatus
      }
    ]
  }
}

export const getAddBuyerParams = (
  payload: AddBuyerRequest
): AuthBuyerParams => {
  const {
    newBuyer: { selectedAccounts },
    accountNumber
  } = payload

  const accountsSendingCardTo = selectedAccounts.filter(({ isSendCard }) => {
    return isSendCard
  })

  const accountsNotSendingCardTo = selectedAccounts.filter(({ isSendCard }) => {
    return !isSendCard
  })
  const authBuyer = []

  if (accountsSendingCardTo.length > 0) {
    authBuyer.push(getAuthBuyerList(payload, accountsSendingCardTo, 'Y'))
  }

  if (accountsNotSendingCardTo.length > 0) {
    authBuyer.push(getAuthBuyerList(payload, accountsNotSendingCardTo))
  }
  return { authBuyer, 'cipher.accountId': accountNumber }
}

export const getRemoveBuyerParams = (
  payload: DeleteBuyerRequest
): RemoveBuyerRequestParams => {
  const { accountNumber, buyers } = payload
  const authBuyer = buyers.map(({ accountRoleId: roleId }) => ({
    newCard: 'N',
    roleId,
    status: 'I'
  }))
  return { authBuyer, 'cipher.accountId': accountNumber }
}

export const getAuthTokenParams = (
  payload: GetAuthTokenParams
): GetAuthTokenRequest => {
  const { authCode } = payload
  return {
    grant_type: 'authorization_code',
    code: authCode,
    client_id: APIGEE_CLIENT_ID,
    redirect_uri: redirectSynchronyCredit,
    code_verifier: getCodeVerifier()
  }
}

export const getEditPaymentParams = (
  payload: PaymentInfo
): EditPaymentRequestParams => {
  const { accountId, amount, email, paymentId, payFromId, scheduledDate } =
    payload
  const { accountIdType, isProx } = userAccountInfo

  const dateParams = isProx ? 'scheduledDate' : 'date'
  return {
    'cipher.accountId': accountId,
    accountIdType,
    amount,
    email,
    paymentId: parseInt(paymentId, 10),
    paymentMethod: {
      'cipher.bankAccountNumber': '',
      'cipher.bankRoutingNumber': '',
      id: payFromId
    },
    [dateParams]: scheduledDate
  }
}

export const schedulePaymentParams = (
  payload: SchedulePaymentRequest,
  paymentId = ''
): SchedulePaymentRequestParams => {
  const { accountID, accountIdType } = userAccountInfo

  return {
    ...payload,
    'cipher.accountId': accountID,
    accountIdType,
    paymentMethod: {
      'cipher.bankAccountNumber': '',
      'cipher.bankRoutingNumber': '',
      id: paymentId
    }
  }
}

export const getInvoiceDownloadParams = (
  payload: InvoiceDownloadRequest
): InvoiceDownloadRequestParams => {
  const {
    selectedItems = [],
    accountNumber,
    lookupField = 'transactionLevel',
    startDate = '',
    endDate = '',
    transactionStatus = '',
    fileFormat,
    childAccountList
  } = payload
  return {
    'cipher.accountId': accountNumber,
    invoiceFormat: fileFormat,
    invoiceDetails: selectedItems.map(({ transactionNumber }) => ({
      transactionNumber
    })),
    accountUniqueIdentifiers: childAccountList?.map(
      accountUniqueIdentifier => ({
        accountUniqueIdentifier
      })
    ),
    startDate,
    endDate,
    transactionStatus,
    LookupField: lookupField
  }
}

export const getMimeType = (
  fileFormat: string,
  name = 'invoices'
): {
  type: string
  fileName: string
} => {
  const formatName = fileFormat === 'excel' ? 'ms-excel' : fileFormat
  let extension = ''
  switch (fileFormat) {
    case 'excel':
      extension = 'xls'
      break
    case 'quicken':
      extension = 'qfx'
      break
    case 'quickbook':
      extension = 'qbo'
      break
    default:
      extension = fileFormat
  }
  const type = `application/${formatName}`
  const fileName = `${name}.${extension}`
  return { type, fileName }
}

export const getSendNewMessageParams = (
  payload: SendNewMessage
): SendNewMessageParams => {
  const { accountId, emailAddress, firstName, subject, content, cardType } =
    payload

  return {
    'cipher.accountId': accountId,
    emailAddress,
    firstName,
    subject,
    content,
    languageId: 'en',
    cardType
  }
}

export const getSwitchLinkedAccount = (
  payload: SwitchAccountRequest
): SwitchLinkedAccountsParams => {
  const { switchAccount, adminUserId } = payload

  return {
    switchAccount,
    adminUserId,
    auth: {
      appId: APIGEE_CLIENT_ID,
      redirectUri: redirectSynchronyCredit,
      scope: 'post-login',
      state: authState,
      codeChallenge,
      codeChallengeMethod
    }
  }
}

export const getEbillEnrollmentParams = (
  payload: EbillInfo
): EbillEnrollmentParams => {
  const {
    emailAddress,
    firstName,
    lastName,
    previousEbpFlag,
    ebpFlag,
    accountNumber
  } = payload
  const { isProx, accountID } = userAccountInfo

  return {
    'cipher.accountId': isProx ? accountNumber : accountID,
    loginUser: 'ESERVICE',
    emailAddress,
    firstName,
    lastName,
    ebpPermFlag: 'Y',
    ebpFlag,
    syncFields: ['6'],
    previousEbpFlag,
    cardType: getCardType()
  }
}

export const getEbillProfileParams = (
  payload: EbillInfo
): EbillProfileParams => {
  const { accountNumber, emailAddress, previousEmailAddress } = payload

  return {
    'cipher.accountId': accountNumber,
    loginUser: 'ESERVICE',
    emailAddress,
    previousEmailAddress
  }
}

export const getDeactiveOnlineUserParams = (
  payload: OnlineUserListRequest
): DeactiveOnlineUserRequestParams => {
  const { userId } = payload
  return {
    userId,
    action: 'deactivate'
  }
}

export const getInvoicePrintParams = (
  payload: PrintInvoiceRequest
): PrintInvoiceRequestParams => {
  const { accountNumber, startDate = '', endDate = '' } = payload
  return {
    'cipher.accountId': accountNumber,
    startDate,
    endDate,
    requestFields: {
      lookupField: ['includeTransactionDetails']
    },
    sortType: '1',
    sortBy: '1'
  }
}

// Registration Eligibility Payload
export const getRegistrationEligibleParams = (
  payload: RegistrationRequest
): RegistrationEligibilityCheckRequestParams => {
  const { accountNumber, authCode, zipCode, iovation } = payload
  return {
    accountInfo: {
      accountNumber,
      authCode,
      zipCode
    },
    authInfo: {
      appId: APIGEE_CLIENT_ID,
      redirectUri: redirectSynchronyCredit,
      scope: 'registration',
      state: authState,
      codeChallenge,
      codeChallengeMethod
    },
    iovation
  }
}

export const getSaveRegistrationParams = (
  payload: SaveUserRegistrationRequest
): SaveRegistrationRequestParams => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    sessionId,
    password,
    dob,
    userId,
    iovation
  } = payload

  return {
    userId,
    firstName,
    lastName,
    phoneNumber,
    email,
    password,
    motherDateOfBirth: dob,
    sessionId,
    authInfo: {
      ...authInformation
    },
    iovation
  }
}

export const getPartialSaveRegistrationParams = (
  payload: PartialSaveUserRegistrationRequest
): PartialRegistrationRequestParams => {
  const { firstName, lastName, phoneNumber, email, sessionId, iovation } =
    payload
  return {
    userId: '',
    firstName,
    lastName,
    phoneNumber,
    email,
    sessionId,
    authInfo: {
      ...authInformation
    },
    iovation
  }
}

export const getUpdatePrivilegeParams = (
  payload: UpdatePrivilegeRequest
): UpdatePrivilegeRequestParams => {
  const {
    sessionId,
    accountList,
    userPrivilegeInfo,
    accountNumber: accountId
  } = payload
  return {
    sessionId,
    accountUniqueIdentifiers: accountList,
    userPrivilegeInfo,
    'cipher.accountId': accountId
  }
}
export const getSwitchAccountInfoParams = (
  payload: SwitchAccountRequestBody
): SwitchAccountInfoParams => {
  const { partialAccountId, accountUniqueIdentifier } = payload
  return {
    'cipher.accountId': partialAccountId,
    accountUniqueIdentifier,
    authInfo: switchAccountAuthInfo
  }
}

export const getCliAutoRequestParams = (
  dataParams: CliRequestPayload
): CliRequestBodyParams => {
  const { accountType } = userAccountInfo
  const {
    accountId,
    requestedCreditLimit,
    currentCreditLimit,
    emailId,
    firstName,
    businessType,
    grossAnnualRevenue
  } = dataParams
  return {
    accountType,
    cardType: getCardType(),
    'cipher.accountId': accountId,
    creditLimitActionFlag: 'I',
    requestedCreditLimit,
    currentCreditLimit,
    emailId,
    firstName,
    businessType,
    grossAnnualRevenue
  }
}

export const getCliAutoEligibilityParams = ({
  accountId,
  currentCreditLimit
}: GetCliAutoEligibilityParams): CliAutoEligibilityRequest => {
  return {
    'cipher.accountId': accountId,
    accountIdType: 'guid',
    creditLimitActionFlag: 'I',
    currentCreditLimit,
    creditLimitAuthFlag: 'N'
  }
}
